<!--
배송단계 동기화 진단
------------------------------------
시스템관리자 전용 진단 도구.
API 원본 조회, 동기화 로직 실행, 배송추적 기록 조회.
-->
<template>
  <div>

    <!-- 검색 폼 -->
    <div class="uk-flex uk-flex-middle uk-margin-small-bottom">
      <label class="uk-margin-small-right" style="font-weight:bold;">House ID:</label>
      <input type="number" class="uk-input uk-form-small" style="width:160px;"
             v-model.number="inputHouseId" placeholder="House ID"
             @keyup.enter="search">
      <label class="uk-margin-small-left uk-margin-small-right" style="font-weight:bold;">운송장번호:</label>
      <input type="text" class="uk-input uk-form-small" style="width:220px;"
             v-model.trim="inputBlno" placeholder="blno"
             @keyup.enter="search">
      <button class="uk-button uk-button-small uk-button-primary uk-margin-small-left"
              @click="search">조회</button>
    </div>

    <!-- 하우스 기본정보 -->
    <div v-if="houseInfo">
      <h5 class="uk-margin-small-top uk-margin-small-bottom" style="font-weight:bold; color:#333;">하우스 기본정보</h5>
      <table class="info-table small uk-margin-small-bottom" style="table-layout: auto; width: 100%;">
        <tbody>
          <tr>
            <th>센터명</th>
            <td><img v-if="regionFlag" :src="regionFlag" style="vertical-align:middle; margin-right:4px;"> {{ houseInfo.region_name }} ({{ houseInfo.region_id }})</td>
            <th>Agent ID</th>
            <td>{{ houseInfo.agent_id }}</td>
            <th>User ID</th>
            <td>{{ houseInfo.user_id }}<span v-if="houseInfo.user_nick"> ({{ houseInfo.user_nick }})</span></td>
          </tr>
          <tr>
            <th>House ID</th>
            <td>{{ houseInfo.id }}</td>
            <th>송장번호 (blno)</th>
            <td>{{ houseInfo.blno || '(없음)' }}</td>
            <th>수취인명</th>
            <td>{{ houseInfo.name || '(없음)' }}</td>
          </tr>
          <tr>
            <th>출고라인</th>
            <td>{{ houseInfo.outline }} ({{ houseInfo.outline_raw }})</td>
            <th>현재 배송단계</th>
            <td>{{ houseInfo.state }} ({{ houseInfo.state_raw }})</td>
            <th>동기화잠금</th>
            <td>{{ houseInfo.off_sync_state }} ({{ houseInfo.off_sync_state_raw }})</td>
          </tr>
        </tbody>
      </table>

      <!-- 진단 버튼 -->
      <div class="uk-margin-small-top uk-margin-bottom">
        <button class="uk-button uk-button-small uk-button-default"
                @click="fetchApiRaw" :disabled="!houseInfo.blno || loading">
          <i class="material-icons" style="font-size:16px; vertical-align:middle;">cloud_download</i>
          API 원본 조회
        </button>
        <button class="uk-button uk-button-small uk-button-danger uk-margin-small-left"
                @click="execSyncLogic" :disabled="loading">
          <i class="material-icons" style="font-size:16px; vertical-align:middle;">sync</i>
          동기화 로직 실행
        </button>
      </div>

      <!-- GONELO 배송추적(OpenApi) -->
      <div class="uk-margin-small-top">
        <div class="uk-flex uk-flex-middle uk-margin-small-bottom">
          <h5 class="uk-margin-remove" style="font-weight:bold; color:#333;">
            GONELO 배송추적(OpenApi)
            <span v-if="trackingsRows !== null" style="color:#999; font-weight:normal;"> ({{ trackingsRows.length }}건)</span>
            <span v-if="trackingsLoading" style="color:#999; font-weight:normal;"> 조회중...</span>
          </h5>
          <button class="uk-button uk-button-small uk-button-default uk-margin-small-left"
                  @click="fetchShippingTrackings" :disabled="trackingsLoading">
            <i class="material-icons" style="font-size:16px; vertical-align:middle;">refresh</i>
            갱신
          </button>
        </div>
        <div v-if="trackingsError" class="uk-alert uk-alert-danger">{{ trackingsError }}</div>
        <table v-if="trackingsRows && trackingsRows.length > 0" class="uk-table uk-table-small uk-table-divider uk-margin-remove-top">
          <thead>
            <tr>
              <th>id</th>
              <th>house_id</th>
              <th>state</th>
              <th>state_text</th>
              <th>tracking_at</th>
              <th>memo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in trackingsRows" :key="row.id">
              <td>{{ row.id }}</td>
              <td>{{ row.house_id }}</td>
              <td>{{ row.state }}</td>
              <td>{{ row.state_text }}</td>
              <td>{{ row.tracking_at || '-' }}</td>
              <td style="white-space:pre-wrap; word-break:break-all;">{{ row.memo || '-' }}</td>
            </tr>
          </tbody>
        </table>
        <div v-else-if="trackingsRows !== null && trackingsRows.length === 0" style="color:#999;">배송추적 기록이 없습니다.</div>
      </div>
    </div>

    <!-- 조회 실패 -->
    <div v-else-if="notFound" class="uk-alert uk-alert-warning uk-margin-small-top">
      <span v-if="searchedHouseId && searchedBlno">
        House ID <strong>{{ searchedHouseId }}</strong> + 운송장번호 <strong>{{ searchedBlno }}</strong>에 해당하는 하우스를 찾을 수 없습니다.
      </span>
      <span v-else-if="searchedHouseId">
        House ID <strong>{{ searchedHouseId }}</strong>에 해당하는 하우스를 찾을 수 없습니다.
      </span>
      <span v-else-if="searchedBlno">
        운송장번호 <strong>{{ searchedBlno }}</strong>에 해당하는 하우스를 찾을 수 없습니다.
      </span>
    </div>

    <!-- API 원본 조회 결과 -->
    <div v-if="apiRawResult" class="uk-margin-top">
      <h5 class="uk-margin-small-bottom" style="font-weight:bold; color:#333;">
        API 원본 조회
        <span v-if="loading" style="color:#999; font-weight:normal;"> 조회중...</span>
      </h5>

      <!-- 제공자 정보 -->
      <div v-if="apiRawResult.provider" class="diag-section">
        <div class="diag-label">제공자</div>
        <div class="diag-content">
          {{ apiRawResult.provider.outline_text }} ({{ apiRawResult.provider.outline }})
          <span v-if="apiRawResult.provider.logic_class" style="color:#666;"> &mdash; {{ apiRawResult.provider.logic_class }}</span>
        </div>
      </div>

      <!-- 에러 메시지 -->
      <div v-if="apiRawResult.error" class="uk-alert uk-alert-danger">{{ apiRawResult.error }}</div>
      <div v-if="apiRawResult.message" class="uk-alert uk-alert-warning">{{ apiRawResult.message }}</div>

      <!-- Step 목록 -->
      <div v-for="(step, idx) in (apiRawResult.steps || [])" :key="idx" class="diag-step">
        <div class="diag-step-title">
          {{ step.step }}
          <span v-if="step.class" style="color:#888; font-weight:normal; font-size:1rem;"> {{ step.class }}</span>
        </div>

        <!-- Request -->
        <div v-if="step.request" class="diag-block">
          <div class="diag-block-label">Request</div>
          <div class="diag-row">
            <span class="diag-row-label">URL</span>
            <span>{{ step.request.method }} {{ step.request.url }}</span>
          </div>
          <div class="diag-row">
            <span class="diag-row-label">Headers</span>
            <pre class="diag-pre">{{ formatJson(step.request.headers) }}</pre>
          </div>
          <div v-if="step.request.body" class="diag-row">
            <span class="diag-row-label">Body</span>
            <pre class="diag-pre">{{ formatJson(step.request.body) }}</pre>
          </div>
        </div>

        <!-- Response -->
        <div v-if="step.response" class="diag-block">
          <div class="diag-block-label diag-block-label--response">Response</div>
          <div class="diag-row">
            <span class="diag-row-label">Status</span>
            <span :style="{ color: step.response.status === 200 ? '#2e7d32' : '#c62828', fontWeight: 'bold' }">{{ step.response.status }}</span>
          </div>
          <div class="diag-row">
            <span class="diag-row-label">Headers</span>
            <pre class="diag-pre">{{ formatJson(step.response.headers) }}</pre>
          </div>
          <div class="diag-row">
            <span class="diag-row-label">Body</span>
            <pre class="diag-pre">{{ formatJson(step.response.body) }}</pre>
          </div>
        </div>

        <!-- Error -->
        <div v-if="step.error" class="diag-block">
          <div class="diag-block-label diag-block-label--error">Error</div>
          <div class="diag-row">
            <span class="diag-row-label">Class</span>
            <span style="color:#c62828;">{{ step.error.class }}</span>
          </div>
          <div class="diag-row">
            <span class="diag-row-label">Message</span>
            <span style="color:#c62828;">{{ step.error.message }}</span>
          </div>
          <div class="diag-row">
            <span class="diag-row-label">Trace</span>
            <pre class="diag-pre" style="color:#c62828;">{{ step.error.trace }}</pre>
          </div>
        </div>
      </div>
    </div>

    <!-- 동기화 로직 실행 결과 -->
    <div v-if="syncLogicResult" class="uk-margin-top">
      <h5 class="uk-margin-small-bottom" style="font-weight:bold; color:#333;">
        동기화 로직 실행
        <span v-if="loading" style="color:#999; font-weight:normal;"> 조회중...</span>
      </h5>

      <!-- 제공자 정보 -->
      <div v-if="syncLogicResult.provider" class="diag-section">
        <div class="diag-label">제공자</div>
        <div class="diag-content">
          {{ syncLogicResult.provider.outline_text }} ({{ syncLogicResult.provider.outline }})
          <span v-if="syncLogicResult.provider.logic_class" style="color:#666;"> &mdash; {{ syncLogicResult.provider.logic_class }}</span>
        </div>
      </div>

      <!-- 에러 (로직 매핑 없음 / 예외) -->
      <div v-if="syncLogicResult.error && typeof syncLogicResult.error === 'string'" class="uk-alert uk-alert-danger">{{ syncLogicResult.error }}</div>
      <div v-else-if="syncLogicResult.error && typeof syncLogicResult.error === 'object'" class="diag-step">
        <div class="diag-step-title" style="background:#ffebee; color:#c62828; border-bottom-color:#ffcdd2;">예외 발생</div>
        <div class="diag-block">
          <div class="diag-block-label diag-block-label--error">Error</div>
          <div class="diag-row">
            <span class="diag-row-label">Class</span>
            <span style="color:#c62828;">{{ syncLogicResult.error.class }}</span>
          </div>
          <div class="diag-row">
            <span class="diag-row-label">Message</span>
            <span style="color:#c62828;">{{ syncLogicResult.error.message }}</span>
          </div>
          <div class="diag-row">
            <span class="diag-row-label">Trace</span>
            <pre class="diag-pre" style="color:#c62828;">{{ syncLogicResult.error.trace }}</pre>
          </div>
        </div>
      </div>

      <!-- MFULEX 결과: tracking.tracks 존재 -->
      <template v-if="syncLogicResult.logic_result && syncLogicResult.logic_result.tracking">
        <!-- 최종 판정 레코드 -->
        <div v-if="syncLogicResult.logic_result.last_record" class="diag-step">
          <div class="diag-step-title">최종 판정 레코드</div>
          <div class="diag-block">
            <div class="diag-row">
              <span class="diag-row-label">일시</span>
              <span>{{ syncLogicResult.logic_result.last_record.date || '-' }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-row-label">상태</span>
              <span>{{ syncLogicResult.logic_result.last_record.status }}</span>
            </div>
          </div>
        </div>

        <!-- 추적 기록 테이블 -->
        <div class="diag-step">
          <div class="diag-step-title">
            추적 기록
            <span style="color:#888; font-weight:normal; font-size:1rem;">
              shippingCode: {{ syncLogicResult.logic_result.tracking.shippingCode }}
              <span v-if="syncLogicResult.logic_result.tracking.orderId"> / orderId: {{ syncLogicResult.logic_result.tracking.orderId }}</span>
            </span>
          </div>
          <table class="uk-table uk-table-small uk-table-divider uk-margin-remove">
            <thead>
              <tr>
                <th>date</th>
                <th>status</th>
                <th>code</th>
                <th>match_state</th>
                <th>match_state_text</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(track, idx) in syncLogicResult.logic_result.tracking.tracks" :key="idx">
                <td>{{ track.date || '-' }}</td>
                <td>{{ track.status }}</td>
                <td>{{ track.code || '-' }}</td>
                <td>{{ track.match_state !== null ? track.match_state : '-' }}</td>
                <td :style="{ color: track.match_state !== null ? '#2e7d32' : '#999' }">{{ track.match_state_text }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- BlChase 결과: chase.records 존재 -->
      <template v-else-if="syncLogicResult.logic_result && syncLogicResult.logic_result.chase">
        <!-- 최종 판정 레코드 -->
        <div v-if="syncLogicResult.logic_result.last_record" class="diag-step">
          <div class="diag-step-title">최종 판정 레코드</div>
          <div class="diag-block">
            <div class="diag-row">
              <span class="diag-row-label">일시</span>
              <span>{{ syncLogicResult.logic_result.last_record.event_at || '-' }}</span>
            </div>
            <div class="diag-row">
              <span class="diag-row-label">상태</span>
              <span>{{ syncLogicResult.logic_result.last_record.state }}</span>
            </div>
          </div>
        </div>

        <!-- 추적 기록 테이블 -->
        <div class="diag-step">
          <div class="diag-step-title">
            추적 기록
            <span style="color:#888; font-weight:normal; font-size:1rem;">
              hawb: {{ syncLogicResult.logic_result.chase.hawb }}
              <span v-if="syncLogicResult.logic_result.chase.stepnm"> / stepnm: {{ syncLogicResult.logic_result.chase.stepnm }}</span>
            </span>
          </div>
          <table class="uk-table uk-table-small uk-table-divider uk-margin-remove">
            <thead>
              <tr>
                <th>seq</th>
                <th>event_at</th>
                <th>state</th>
                <th>desc</th>
                <th>locate</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(record, idx) in syncLogicResult.logic_result.chase.records" :key="idx">
                <td>{{ record.seq || '-' }}</td>
                <td>{{ record.event_at || '-' }}</td>
                <td>{{ record.state || '-' }}</td>
                <td>{{ record.desc || '-' }}</td>
                <td>{{ record.locate || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- 결과 없음 (tracking/chase 모두 null) -->
      <div v-else-if="syncLogicResult.logic_result && !syncLogicResult.logic_result.tracking && !syncLogicResult.logic_result.chase"
           class="uk-alert uk-alert-warning">
        API 조회 결과가 없습니다 (tracking/chase 데이터 null).
      </div>
    </div>

  </div>
</template>

<script>
import { get_region } from '../../../classes/region-factory';

export default {
  props: {
    csrfToken: {
      type: String,
      required: true,
    },
    initHouseInfo: {
      type: [Object, String],
      default: null,
    },
    initHouseId: {
      type: [Number, String],
      default: null,
    },
    initBlno: {
      type: String,
      default: null,
    },
    // 서버에서 조회했으나 결과 없음
    initNotFound: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    var parsed = this.initHouseInfo;
    if (typeof parsed === 'string' && parsed) {
      try { parsed = JSON.parse(parsed); } catch (e) { parsed = null; }
    }

    return {
      inputHouseId: this.initHouseId ? Number(this.initHouseId) : null,
      inputBlno: this.initBlno || '',
      houseInfo: parsed || null,
      notFound: this.initNotFound,
      searchedHouseId: this.initHouseId || null,
      searchedBlno: this.initBlno || null,

      loading: false,
      apiRawResult: null,
      syncLogicResult: null,

      trackingsLoading: false,
      trackingsRows: null,
      trackingsError: null,
    };
  },
  mounted: function () {
    if (this.houseInfo) {
      this.fetchShippingTrackings();
    }
  },
  computed: {
    regionFlag: function () {
      if (!this.houseInfo) return null;
      var region = get_region(this.houseInfo.region_id);
      return region ? region.flag16 : null;
    },
  },
  methods: {

    /**
     * 조회 (URL 변경하여 페이지 리로드)
     */
    search: function () {
      if (!this.inputHouseId && !this.inputBlno) return;

      var params = [];
      if (this.inputHouseId) {
        params.push('house_id=' + this.inputHouseId);
      }
      if (this.inputBlno) {
        params.push('blno=' + encodeURIComponent(this.inputBlno));
      }
      window.location.href = '/system/sync-diagnosis?' + params.join('&');
    },

    /**
     * JSON 포맷팅
     */
    formatJson: function (data) {
      if (typeof data === 'string') return data;
      return JSON.stringify(data, null, 2);
    },

    /**
     * API 원본 조회
     */
    fetchApiRaw: function () {
      this.loading = true;
      this.apiRawResult = null;
      this.syncLogicResult = null;

      $.post('/system/sync-diagnosis/api-raw', {
        _token: this.csrfToken,
        house_id: this.houseInfo.id,
      }).done(function (data) {
        this.apiRawResult = data;
        this.loading = false;
      }.bind(this)).fail(function (xhr) {
        this.apiRawResult = { error: xhr.status + ' ' + xhr.statusText };
        this.loading = false;
      }.bind(this));
    },

    /**
     * 동기화 로직 실행
     */
    execSyncLogic: function () {
      if (!confirm('동기화 로직을 실행하면 실제 DB가 변경될 수 있습니다.\n계속하시겠습니까?')) {
        return;
      }

      this.loading = true;
      this.apiRawResult = null;
      this.syncLogicResult = null;

      $.post('/system/sync-diagnosis/sync-logic', {
        _token: this.csrfToken,
        house_id: this.houseInfo.id,
      }).done(function (data) {
        this.syncLogicResult = data;
        this.loading = false;
        this.fetchShippingTrackings();
      }.bind(this)).fail(function (xhr) {
        this.syncLogicResult = { error: xhr.status + ' ' + xhr.statusText };
        this.loading = false;
      }.bind(this));
    },

    /**
     * GONELO 배송추적(OpenApi) 조회
     */
    fetchShippingTrackings: function () {
      this.trackingsLoading = true;
      this.trackingsError = null;

      $.post('/system/sync-diagnosis/shipping-trackings', {
        _token: this.csrfToken,
        house_id: this.houseInfo.id,
      }).done(function (data) {
        this.trackingsRows = data.rows || [];
        this.trackingsLoading = false;
      }.bind(this)).fail(function (xhr) {
        this.trackingsError = xhr.status + ' ' + xhr.statusText;
        this.trackingsRows = null;
        this.trackingsLoading = false;
      }.bind(this));
    },
  },
}
</script>

<style scoped>
/* 진단 결과 공통 */
.diag-pre {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  padding: 8px 10px;
  font-size: 1rem;
  line-height: 1.5;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 0;
  max-height: 400px;
}

/* 제공자 정보 */
.diag-section {
  padding: 8px 12px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  margin-bottom: 12px;
  font-size: 1rem;
}
.diag-label {
  font-weight: bold;
  font-size: 1rem;
  color: #666;
  text-transform: uppercase;
  margin-bottom: 2px;
}
.diag-content {
  color: #333;
}

/* Step 카드 */
.diag-step {
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  margin-bottom: 12px;
  overflow: hidden;
}
.diag-step-title {
  background: #e8eaf6;
  padding: 8px 12px;
  font-weight: bold;
  font-size: 1rem;
  color: #283593;
  border-bottom: 1px solid #c5cae9;
}

/* Request / Response / Error 블록 */
.diag-block {
  border-top: 1px solid #e0e0e0;
}
.diag-block-label {
  background: #e3f2fd;
  padding: 4px 12px;
  font-weight: bold;
  font-size: 1rem;
  color: #1565c0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.diag-block-label--response {
  background: #e8f5e9;
  color: #2e7d32;
}
.diag-block-label--error {
  background: #ffebee;
  color: #c62828;
}

/* 행 (URL, Headers, Body 등) */
.diag-row {
  padding: 4px 12px;
  font-size: 1rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: flex-start;
}
.diag-row-label {
  flex-shrink: 0;
  width: 70px;
  font-weight: bold;
  color: #555;
  padding-top: 2px;
}
.diag-row > span:last-child,
.diag-row > pre {
  flex: 1;
  min-width: 0;
}
</style>
